/* Rare motion layer — curtain intro, hero word reveals, staggered scroll entrances,
   parallax and gold sparkles.
   Lightweight, dependency-free, and fully skipped for prefers-reduced-motion. */
(() => {
  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  /* ---------- Curtain intro ---------- */
  const startHero = () => root.classList.add('rare-hero-go');

  const setupCurtain = () => {
    const curtain = document.querySelector('[data-rare-curtain]');
    if (!curtain || !root.classList.contains('rare-intro')) {
      curtain?.remove();
      startHero();
      return;
    }

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      root.classList.remove('rare-intro');
      curtain.remove();
    };

    // The hero copy starts rising just as the drapes part.
    const heroTimer = window.setTimeout(startHero, 1700);
    const doneTimer = window.setTimeout(finish, 3150);

    const skip = () => {
      if (curtain.classList.contains('is-skipped')) return;
      curtain.classList.add('is-skipped');
      window.clearTimeout(heroTimer);
      window.clearTimeout(doneTimer);
      window.setTimeout(startHero, 250);
      window.setTimeout(finish, 950);
    };

    curtain.addEventListener('click', skip);
    document.addEventListener('keydown', skip, { once: true });
  };

  /* ---------- Word splitting ---------- */
  const splitWords = (element, baseDelay = 0) => {
    if (!element || element.dataset.rareSplit || element.childElementCount > 0) return;
    const text = element.textContent.trim();
    if (!text) return;

    element.dataset.rareSplit = 'true';
    element.setAttribute('aria-label', text);
    element.style.setProperty('--rare-base-delay', `${baseDelay}ms`);
    element.textContent = '';

    text.split(/\s+/).forEach((word, index, words) => {
      const outer = document.createElement('span');
      const inner = document.createElement('span');
      outer.className = 'rare-word';
      outer.setAttribute('aria-hidden', 'true');
      inner.textContent = word;
      inner.style.setProperty('--i', index);
      outer.appendChild(inner);
      element.appendChild(outer);
      if (index < words.length - 1) element.appendChild(document.createTextNode(' '));
    });
  };

  const setupHero = (scope = document) => {
    scope.querySelectorAll('.rare-hero__heading').forEach((heading) => splitWords(heading, 120));
    if (scope.querySelector('.rare-hero')) root.classList.add('rare-hero-armed');

    scope.querySelectorAll('.rare-hero').forEach((hero) => addSparkles(hero, 10));
  };

  const setupHeadings = (scope = document) => {
    scope
      .querySelectorAll(
        'main .shopify-section:not(.rare-hero-section) h2, .rare-category-rail__heading, .rare-editorial__heading'
      )
      .forEach((heading) => {
        if (heading.closest('.rare-hero')) return;
        splitWords(heading, 100);
        if (heading.dataset.rareSplit) heading.classList.add('rare-heading-split');
      });
  };

  /* ---------- Sparkles ---------- */
  function addSparkles(host, count) {
    if (!host || host.querySelector(':scope > .rare-sparkles')) return;
    const layer = document.createElement('div');
    layer.className = 'rare-sparkles';
    layer.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < count; i += 1) {
      const dot = document.createElement('i');
      dot.style.left = `${Math.random() * 100}%`;
      dot.style.top = `${20 + Math.random() * 75}%`;
      dot.style.setProperty('--sz', `${2 + Math.random() * 3}px`);
      dot.style.setProperty('--d', `${4 + Math.random() * 5}s`);
      dot.style.setProperty('--delay', `${Math.random() * 6}s`);
      layer.appendChild(dot);
    }
    host.appendChild(layer);
  }

  /* ---------- Staggered items ---------- */
  const staggerGroups = [
    '.resource-list__item',
    '.rare-category-card',
    '.rare-social-tile',
    '.rare-benefit',
    '.rare-testimonial',
    '.rare-footer__grid > *',
  ];

  const setupStagger = (scope = document) => {
    root.classList.add('rare-stagger-ready');
    staggerGroups.forEach((selector) => {
      const byParent = new Map();
      scope.querySelectorAll(`main ${selector}, footer ${selector}`).forEach((item) => {
        if (item.hasAttribute('data-rare-stagger')) return;
        const siblings = byParent.get(item.parentElement) || [];
        siblings.push(item);
        byParent.set(item.parentElement, siblings);
      });
      byParent.forEach((items) => {
        items.forEach((item, index) => {
          item.setAttribute('data-rare-stagger', '');
          item.style.setProperty('--i', Math.min(index, 8));
        });
      });
    });

    // Footer sections are not observed by rare-effects.js, so watch them here.
    const footerObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('rare-is-visible');
          footerObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.1 }
    );
    scope.querySelectorAll('footer .shopify-section').forEach((section) => footerObserver.observe(section));
  };

  /* ---------- Parallax & hero drift (one rAF loop) ---------- */
  const setupScrollEffects = () => {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const layers = [
      ...[...document.querySelectorAll('.rare-parallax__image')].map((el) => ({ el, host: el.closest('.rare-parallax'), amount: 0.16 })),
      ...(isMobile
        ? []
        : [...document.querySelectorAll('.rare-editorial__media-main')].map((el) => ({
            el,
            host: el.closest('.rare-editorial__media'),
            amount: 0.1,
          }))),
    ].filter((layer) => layer.host);

    const hero = document.querySelector('.rare-hero');
    const heroMedia = hero ? [...hero.querySelectorAll('.rare-hero__media')] : [];

    // Horizon scrolls inside .page-wrapper on desktop and the window on mobile.
    const wrapper = document.querySelector('.page-wrapper');
    const scrollState = () => {
      if (wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1 && getComputedStyle(wrapper).overflowY !== 'visible') {
        return { top: wrapper.scrollTop, max: wrapper.scrollHeight - wrapper.clientHeight };
      }
      return { top: window.scrollY, max: document.documentElement.scrollHeight - window.innerHeight };
    };

    let ticking = false;
    const update = () => {
      ticking = false;
      const viewport = window.innerHeight;
      const { top: scrollTop } = scrollState();

      layers.forEach(({ el, host, amount }) => {
        const rect = host.getBoundingClientRect();
        if (rect.bottom < -100 || rect.top > viewport + 100) return;
        const centre = (rect.top + rect.height / 2 - viewport / 2) / (viewport + rect.height);
        el.style.setProperty('--rare-py', `${(-centre * rect.height * amount).toFixed(1)}px`);
      });

      if (heroMedia.length && scrollTop < viewport * 1.2) {
        const shift = `${(scrollTop * 0.3).toFixed(1)}px`;
        heroMedia.forEach((media) => media.style.setProperty('--rare-hero-shift', shift));
      }
    };

    const request = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', request, { passive: true });
    wrapper?.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request, { passive: true });
    update();
  };

  /* ---------- Boot ---------- */
  const boot = () => {
    if (reducedMotion) {
      document.querySelector('[data-rare-curtain]')?.remove();
      root.classList.remove('rare-intro');
      return;
    }

    root.classList.add('rare-split-ready');
    setupHero();
    setupHeadings();
    setupStagger();
    document.querySelectorAll('.rare-parallax').forEach((section) => addSparkles(section, 8));
    setupScrollEffects();
    setupCurtain();

    // Headings in sections that are already on screen (or when rare-effects.js is absent).
    const headingObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('rare-in');
          headingObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.2 }
    );
    document.querySelectorAll('.rare-heading-split').forEach((heading) => headingObserver.observe(heading));

    if (designMode) {
      document.addEventListener('shopify:section:load', (event) => {
        setupHero(event.target);
        setupHeadings(event.target);
        setupStagger(event.target);
        event.target.querySelectorAll('.rare-heading-split').forEach((heading) => heading.classList.add('rare-in'));
        startHero();
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
